// ---------------------------------------------------------------------------
// HorizontalBarChart -- Capsule Bars with Rank Medals
// ---------------------------------------------------------------------------
// Renders a sorted list of horizontal capsule bars with:
//   - Automatic sorting (descending by value)
//   - Rank medals for top 3 items
//   - Staggered cascade animation
//   - Sequential Felton palette colors
// ---------------------------------------------------------------------------

import React, { useMemo } from 'react';
import { CapsuleBar } from '../primitives/CapsuleBar';
import { CapsuleLegend } from '../primitives/CapsuleLegend';
import type { LegendItem } from '../primitives/CapsuleLegend';
import { ChartCard } from './ChartCard';
import { CHART_ANIMATION } from '../constants/chartAnimation';
import { getPlatformIcon } from '../../shared/platformIcons';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface BarDataItem {
  readonly label: string;
  readonly value: number;
}

export interface HorizontalBarChartProps {
  /** Data items to render as bars */
  readonly data: ReadonlyArray<BarDataItem>;
  /** Card title */
  readonly title: string;
  /** Optional card subtitle */
  readonly subtitle?: string;
  /** Show rank medals for top 3 */
  readonly showRankMedals?: boolean;
  /** Show numeric values next to bars */
  readonly showValues?: boolean;
  /** Maximum number of bars to display */
  readonly maxItems?: number;
  /** Additional CSS class on the container */
  readonly className?: string;
}

// ---------------------------------------------------------------------------
// Rank styling (simple numeric ranks)
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Number formatter
// ---------------------------------------------------------------------------

function formatNumber(n: number): string {
  return n.toLocaleString('en-US');
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const HorizontalBarChart: React.FC<HorizontalBarChartProps> = ({
  data,
  title,
  subtitle,
  showRankMedals = true,
  showValues = true,
  maxItems,
  className,
}) => {
  // Sort descending and limit
  const sortedData = useMemo(() => {
    const sorted = [...data].sort((a, b) => b.value - a.value);
    return maxItems ? sorted.slice(0, maxItems) : sorted;
  }, [data, maxItems]);

  const maxValue = useMemo(
    () => sortedData.reduce((max, item) => Math.max(max, item.value), 0),
    [sortedData]
  );

  const totalValue = useMemo(
    () => sortedData.reduce((sum, item) => sum + item.value, 0),
    [sortedData]
  );

  // Resolve platform colors for items (null for non-platform labels)
  const platformColors = useMemo(
    () => sortedData.map(item => getPlatformIcon(item.label)?.color ?? undefined),
    [sortedData]
  );

  // Build legend items
  const legendItems: ReadonlyArray<LegendItem> = useMemo(
    () =>
      sortedData.map((item, index) => ({
        label: item.label,
        value: item.value,
        colorIndex: index,
        color: platformColors[index],
      })),
    [sortedData, platformColors]
  );

  // Empty state when no data
  if (sortedData.length === 0) {
    return (
      <ChartCard title={title} subtitle={subtitle} className={className}>
        <div className="flex items-center justify-center min-h-[120px] text-v11-text-muted text-sm">
          No data available for this period
        </div>
      </ChartCard>
    );
  }

  return (
    <ChartCard title={title} subtitle={subtitle} className={className}>
      <div className="space-y-2.5">
        {sortedData.map((item, index) => (
          <div key={`${item.label}-${index}`} className="flex items-start gap-2">
            {/* Numeric rank */}
            {showRankMedals && (
              <span className="w-4 text-center text-[11px] font-semibold tabular-nums text-v11-text-muted mt-0.5 flex-shrink-0">
                {index + 1}
              </span>
            )}

            {/* Capsule Bar (já tem label interno e valor) */}
            <div className="flex-1">
              <CapsuleBar
                value={item.value}
                maxValue={maxValue}
                totalValue={totalValue}
                colorIndex={index}
                colorOverride={platformColors[index]}
                size="md"
                label={item.label}
                showValue={showValues}
                animationDelay={index * CHART_ANIMATION.staggerDelay}
              />
            </div>
          </div>
        ))}
      </div>
    </ChartCard>
  );
};
